import styled from '@emotion/styled';
import { Icon, IconNames, rem } from '@triumph/design-system';
import { ReactNode, useState } from 'react';
import { usePermissions } from '@/components/providers/PermissionsContextProvider';
import {
    ELEVATED_PERMISSIONS_REQUIRED_TEXT,
    Feature,
} from '@/components/shared/constants/features';

const PLACEHOLDER_WIDTH = rem(180);
const PLACEHOLDER_HEIGHT = rem(80);

const Wrapper = styled.span<{ $hidden: boolean; $interactive: boolean }>`
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    vertical-align: middle;
    min-width: ${PLACEHOLDER_WIDTH};
    min-height: ${PLACEHOLDER_HEIGHT};
    color: ${({ theme }) => theme.colors.greyscale.gray50};
    font-size: ${({ theme }) => theme.typography.fontSizes.label.large};

    ${({ $hidden, $interactive, theme }) =>
        $hidden &&
        `
            border: ${rem(1)} dashed ${theme.colors.greyscale.gray15};
            border-radius: ${rem(4)};
            background: ${theme.colors.greyscale.gray5};
            ${
                $interactive
                    ? `
                        cursor: pointer;

                        &:hover {
                            background: ${theme.colors.greyscale.gray10};
                        }
                    `
                    : ''
            }
        `}

    &:focus-within {
        outline: ${rem(2)} solid ${({ theme }) => theme.colors.primary.actionDefault};
        outline-offset: ${rem(2)};
    }
`;

const ToggleButton = styled.button`
    position: absolute;
    top: ${rem(6)};
    left: ${rem(6)};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: ${rem(4)};
    background: transparent;
    border: none;
    border-radius: ${rem(4)};
    cursor: pointer;
    z-index: 1;

    &:hover {
        background: ${({ theme }) => theme.colors.greyscale.gray10};
    }

    &:focus-visible {
        outline: ${rem(2)} solid ${({ theme }) => theme.colors.primary.actionDefault};
        outline-offset: ${rem(2)};
    }
`;

interface MaskedComponentProps {
    children: ReactNode;
    placeholderText?: string;
    requiredFeatures?: Feature[];
}

const RestrictedText = styled.span`
    padding: ${rem(8)};
    text-align: center;
`;

export const MaskedComponent = ({
    children,
    placeholderText = 'Hidden',
    requiredFeatures = [],
}: MaskedComponentProps) => {
    const [show, setShow] = useState(false);
    const { hasFeature } = usePermissions();

    const hasAccess = requiredFeatures.every((feature) => hasFeature(feature));

    if (!hasAccess) {
        return (
            <Wrapper $hidden $interactive={false} data-testid="masked-component-restricted">
                <RestrictedText>{ELEVATED_PERMISSIONS_REQUIRED_TEXT}</RestrictedText>
            </Wrapper>
        );
    }

    const handleWrapperClick = () => {
        if (!show) setShow(true);
    };

    return (
        <Wrapper
            $hidden={!show}
            $interactive
            onClick={handleWrapperClick}
            role={!show ? 'button' : undefined}
            tabIndex={!show ? 0 : undefined}
            onKeyDown={
                !show
                    ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setShow(true);
                          }
                      }
                    : undefined
            }
            aria-label={!show ? `Reveal: ${placeholderText}` : undefined}
        >
            <ToggleButton
                type="button"
                onClick={(e) => {
                    e.stopPropagation();
                    setShow((prev) => !prev);
                }}
                aria-label={show ? 'Hide' : `Reveal: ${placeholderText}`}
                data-testid="masked-component-toggle"
            >
                <Icon
                    name={show ? IconNames.Visibility_Show : IconNames.Visibility_Hide}
                    size={16}
                    id="masked-component-toggle-icon"
                    color="#0a1e3a"
                />
            </ToggleButton>
            {/* FullStory privacy: elements with "me-is-sensitive-mask-me" are masked in session recordings, applied unconditionally so revealed content is never captured */}
            {show ? (
                <span className="me-is-sensitive-mask-me">{children}</span>
            ) : (
                <span>{placeholderText}</span>
            )}
        </Wrapper>
    );
};
